import { useCallback, useEffect, useRef } from 'react';

import type { AudioFormat } from '../../api/cc-smarts/getAudio';
import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import { AudioPlayerStreamingManager } from '../effects/AudioPlayerStreamingManager';

import { useAudioPlayerStore } from './useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from './useAudioPlayerStreamingStore';

type AudioPlayerStreamingConfig = {
	locale: string;
	type: AudioFeatureType;
	format: AudioFormat;
	activationId: string;
	product: string;
	cloudId: string;
};

export function useAudioPlayerStreaming({
	activationId,
	product,
	cloudId,
	locale,
	type,
	format,
}: AudioPlayerStreamingConfig) {
	const { state, updateAudioPlayerStreamingStore } = useAudioPlayerStreamingStore();

	const { activeContent } = useAudioPlayerStore();

	const audioElementRef = useRef<HTMLAudioElement>(null);
	const audioPlayerStreamingManager = useRef(
		new AudioPlayerStreamingManager({
			requestParams: {
				activationId,
				product,
				cloudId,
				locale,
				contentId: activeContent.contentId,
				contentType: activeContent.contentType,
				type,
				format,
			},
			onAudioStreamingChange: (action) => updateAudioPlayerStreamingStore(action),
		}),
	);

	const fetchAudio = useCallback(async () => {
		await audioPlayerStreamingManager.current.fetchAudio();
		audioPlayerStreamingManager.current.streamAudioToMediaSource(audioElementRef.current);
	}, [audioPlayerStreamingManager]);

	useEffect(() => {
		audioPlayerStreamingManager.current.resetState();
		audioPlayerStreamingManager.current.validateRequestParams();
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			audioPlayerStreamingManager.current.disposeStream();
		};
	}, []);

	return { state, fetchAudio, audioElementRef };
}
